import { useState, useCallback } from 'react';

const useGeolocation = () => {
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [status, setStatus] = useState('idle'); // idle, requesting, success, denied, error
  const [error, setError] = useState('');

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      setStatus('error');
      return Promise.reject(new Error('Geolocation not supported'));
    }

    setStatus('requesting');
    setError('');

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = {
            lat: parseFloat(latitude.toFixed(6)),
            lng: parseFloat(longitude.toFixed(6))
          };
          
          setLocation(newLocation);
          setStatus('success');
          resolve(newLocation);
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              setStatus('denied');
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable';
              setStatus('error');
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out';
              setStatus('error');
              break;
            default:
              errorMessage = 'An unknown error occurred';
              setStatus('error');
              break;
          }
          
          setError(errorMessage);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 600000 // 10 minutes
        }
      );
    });
  }, []);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
      );
      const data = await response.json();
      
      if (data && data.locality) {
        return {
          city: data.locality,
          state: data.principalSubdivision,
          country: data.countryName,
          formatted: `${data.locality}, ${data.principalSubdivision}, ${data.countryName}`
        };
      }
      
      return null;
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setLocation({ lat: null, lng: null });
    setStatus('idle');
    setError('');
  }, []);

  return {
    location,
    status,
    error,
    getCurrentLocation,
    reverseGeocode,
    reset,
    isLoading: status === 'requesting',
    isSuccess: status === 'success',
    isDenied: status === 'denied',
    hasError: status === 'error'
  };
};

export default useGeolocation;